import { Dialog, Input } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useCreateChecklistCategories } from '../../queries/use-create-checklist-categories';
import { parseTitle } from '../../utilities/formatting';

type NewCategoryConfirmProps = {
    checklistId: string;
    isOpen: boolean;
    onClose: () => void;
    onCreate: () => void;
};

function NewCategoryConfirm({ checklistId, isOpen, onClose, onCreate }: NewCategoryConfirmProps): JSX.Element {
    const [name, setName] = useState<{ value?: string; errorMessage?: string }>();
    const createChecklistCategoriesQuery = useCreateChecklistCategories(checklistId);

    function handleClose(): void {
        onClose();
        setName({});
    }

    function handleSubmit(): void {
        if (!name?.value) {
            setName({ errorMessage: 'A category name must be provided.' });
            return;
        }

        createChecklistCategoriesQuery.mutate(
            {
                categories: [{ name: name.value, reference: parseTitle(name.value, 3) }],
            },
            {
                onSuccess: () => {
                    onCreate();
                    toastNotification('success', 'New category created!');
                },
                onError: (err) => {
                    setName((prev) => ({ ...prev, errorMessage: err.response?.data.message }));
                    toastNotification('error', 'Unable to create new category!');
                },
            }
        );
    }

    return (
        <Dialog
            type="success"
            title="New Category"
            description="Do you want to create a new category for this checklist? You can assign items to this category after creation."
            isOpen={isOpen}
            primaryAction="Create"
            secondaryAction="Cancel"
            onClose={handleClose}
            onSecondaryActionClick={handleClose}
            onPrimaryActionClick={handleSubmit}
            isLoading={createChecklistCategoriesQuery.isLoading}
        >
            <div className="pr-6 pb-4">
                <Input
                    type="text"
                    placeholder="e.g. Tax-related items"
                    label="Name"
                    onChange={(e): void => setName({ value: e.target.value, errorMessage: undefined })}
                    errorMessage={name?.errorMessage}
                    autoFocus
                />
            </div>
        </Dialog>
    );
}

export default NewCategoryConfirm;
